import React, { useEffect, useState } from "react";
import { collection, query, where, getDocs, updateDoc, doc, deleteDoc, writeBatch } from "firebase/firestore";
import { ref, deleteObject } from "firebase/storage";
import { db, storage } from "../firebase";
import ConfirmDialog from "./ConfirmDialog";

const PhotoModeration = ({ shopId }) => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [activeTab, setActiveTab] = useState("pending"); // "pending", "approved", "rejected"
  const [allPhotos, setAllPhotos] = useState({
    pending: [],
    approved: [],
    rejected: []
  });
  const [indexBuilding, setIndexBuilding] = useState(false);
  const [indexUrl, setIndexUrl] = useState("");
  const [retryIn, setRetryIn] = useState(0);
  const [retryAttempts, setRetryAttempts] = useState(0);
  const [reloadTick, setReloadTick] = useState(0);
  const [confirmState, setConfirmState] = useState(null); // { title, message, onConfirm }

  // Alle foto's ophalen en categoriseren
  const loadAllPhotos = async () => {
    if (!shopId) return;
    
    setLoading(true);
    setMessage("");
    
    try {
      const photosRef = collection(db, "photos");
      
      // Haal alle foto's op voor deze shop (zowel shopId als zaakId)
      const q1 = query(photosRef, where("shopId", "==", shopId));
      const q2 = query(photosRef, where("zaakId", "==", shopId));
      
      const [snapshot1, snapshot2] = await Promise.all([getDocs(q1), getDocs(q2)]);
      
      const allPhotosData = [];
      
      // Verzamel alle foto's
      snapshot1.docs.forEach(doc => {
        allPhotosData.push({ id: doc.id, ...doc.data(), source: 'shopId' });
      });
      
      snapshot2.docs.forEach(doc => {
        if (!allPhotosData.find(p => p.id === doc.id)) {
          allPhotosData.push({ id: doc.id, ...doc.data(), source: 'zaakId' });
        }
      });
      
      
      // Categoriseer foto's
      const categorized = {
        pending: [],
        approved: [],
        rejected: []
      };
      
      allPhotosData.forEach(photo => {
        const data = photo;
        
        // Bepaal status
        let status = 'pending';
        if (data.approved === true || data.status === 'approved') {
          status = 'approved';
        } else if (data.rejected === true || data.status === 'rejected') {
          status = 'rejected';
        }
        
        const photoData = {
          id: data.id,
          ...data,
          createdAt: data.createdAt?.toDate() || data.timestamp?.toDate() || new Date(),
          photoUrl: data.photoUrl || data.imageUrl || '',
          userName: data.userName || 'Onbekende gebruiker',
          status: status
        };
        
        categorized[status].push(photoData);
      });
      
      // Sorteer op datum (nieuwste eerst)
      Object.keys(categorized).forEach(key => {
        categorized[key].sort((a, b) => b.createdAt - a.createdAt);
      });
      
      setAllPhotos(categorized);
      setPhotos(categorized[activeTab]);
      
      const totalPending = categorized.pending.length;
      const totalApproved = categorized.approved.length;
      const totalRejected = categorized.rejected.length;
      
      if (totalPending === 0 && totalApproved === 0 && totalRejected === 0) {
        setMessage("Geen foto's gevonden voor deze shop.");
      } else {
        setMessage(`${totalPending} wachtend, ${totalApproved} goedgekeurd, ${totalRejected} afgekeurd`);
      }
    } catch (error) {
      console.error("Error loading photos:", error);
      const msg = error?.message || "";
      const isIndexIssue = error?.code === 'failed-precondition' || msg.includes('requires an index') || msg.includes('index is currently building');
      if (isIndexIssue) {
        const m = msg.match(/https?:\/\/\S+/);
        setIndexUrl(m ? m[0] : "");
        setIndexBuilding(true);
        setMessage("Index wordt gebouwd voor foto's. We proberen het zo weer.");
        const attempts = retryAttempts + 1;
        setRetryAttempts(attempts);
        const waitSec = Math.min(30, 5 * attempts);
        setRetryIn(waitSec);
        let remaining = waitSec;
        const timer = setInterval(() => {
          remaining -= 1;
          setRetryIn(prev => (prev > 0 ? prev - 1 : 0));
          if (remaining <= 0) clearInterval(timer);
        }, 1000);
        if (attempts <= 6) {
          setTimeout(() => setReloadTick(t => t + 1), waitSec * 1000);
        }
      } else {
        setMessage("Fout bij laden foto's: " + error.message);
        setAllPhotos({ pending: [], approved: [], rejected: [] });
        setPhotos([]);
      }
    }
    setLoading(false);
  };

  // Foto status wijzigen
  const changePhotoStatus = async (photoId, newStatus) => {
    try {
      const photoRef = doc(db, "photos", photoId);
      
      const updateData = {};
      
      if (newStatus === 'approved') {
        updateData.approved = true;
        updateData.status = 'approved';
        updateData.approvedAt = new Date();
        updateData.approvedBy = shopId;
        updateData.rejected = false;
        updateData.rejectedAt = null;
      } else if (newStatus === 'rejected') {
        updateData.approved = false;
        updateData.status = 'rejected';
        updateData.rejected = true;
        updateData.rejectedAt = new Date();
        updateData.rejectedBy = shopId;
        updateData.approvedAt = null;
      } else if (newStatus === 'pending') {
        updateData.approved = false;
        updateData.status = 'pending';
        updateData.rejected = false;
        updateData.approvedAt = null;
        updateData.rejectedAt = null;
      }
      
      await updateDoc(photoRef, updateData);
      
      // Update local state
      await loadAllPhotos();
      
      const statusText = newStatus === 'approved' ? 'goedgekeurd' : 
                        newStatus === 'rejected' ? 'afgekeurd' : 'terug naar wachtend';
      setMessage(`Foto ${statusText}!`);
      
    } catch (error) {
      setMessage("Fout bij wijzigen status: " + error.message);
    }
  };

  // Foto permanent verwijderen (alleen voor afgewezen foto's)
  const deletePhoto = (photoId, photoUrl) => {
    setConfirmState({
      title: "Foto verwijderen",
      message: "Weet je zeker dat je deze foto permanent wilt verwijderen? Dit kan niet ongedaan worden gemaakt.",
      danger: true,
      confirmLabel: "Verwijderen",
      onConfirm: () => doDeletePhoto(photoId, photoUrl),
    });
  };

  const doDeletePhoto = async (photoId, photoUrl) => {
    setConfirmState(null);
    try {
      // Verwijder uit Firestore
      const photoRef = doc(db, "photos", photoId);
      await deleteDoc(photoRef);
      
      // Verwijder uit Storage
      try {
        const storageRef = ref(storage, photoUrl);
        await deleteObject(storageRef);
      } catch (storageError) {
        console.warn("Could not delete from storage:", storageError);
      }
      
      // Update local state
      await loadAllPhotos();
      setMessage("Foto permanent verwijderd!");
      
    } catch (error) {
      setMessage("Fout bij verwijderen foto: " + error.message);
    }
  };

  // Automatische cleanup van oude afgewezen foto's (10 dagen)
  const cleanupOldRejectedPhotos = async () => {
    try {
      const tenDaysAgo = new Date();
      tenDaysAgo.setDate(tenDaysAgo.getDate() - 10);

      const photosRef = collection(db, "photos");
      const q1 = query(
        photosRef,
        where("shopId", "==", shopId),
        where("rejected", "==", true),
        where("rejectedAt", "<", tenDaysAgo)
      );
      const q2 = query(
        photosRef,
        where("zaakId", "==", shopId),
        where("status", "==", "rejected"),
        where("rejectedAt", "<", tenDaysAgo)
      );

      const [snapshot1, snapshot2] = await Promise.all([getDocs(q1), getDocs(q2)]);

      const photosToDelete = [...snapshot1.docs, ...snapshot2.docs];

      if (photosToDelete.length === 0) {
        setMessage("Geen oude afgewezen foto's gevonden om op te ruimen.");
        return;
      }

      setConfirmState({
        title: "Oude foto's opruimen",
        message: `Weet je zeker dat je ${photosToDelete.length} oude afgewezen foto's wilt verwijderen? Dit kan niet ongedaan worden gemaakt.`,
        danger: true,
        confirmLabel: "Opruimen",
        onConfirm: () => doCleanupOldRejectedPhotos(photosToDelete),
      });
    } catch (error) {
      setMessage("Fout bij opruimen foto's: " + error.message);
    }
  };

  const doCleanupOldRejectedPhotos = async (photosToDelete) => {
    setConfirmState(null);
    try {
      // Verwijder alle oude foto's
      const batch = writeBatch(db);
      const storagePromises = [];
      
      photosToDelete.forEach(doc => {
        batch.delete(doc.ref);
        
        const photoData = doc.data();
        const photoUrl = photoData.photoUrl || photoData.imageUrl;
        
        if (photoUrl) {
          try {
            const storageRef = ref(storage, photoUrl);
            storagePromises.push(deleteObject(storageRef));
          } catch (error) {
            console.warn("Could not delete from storage:", error);
          }
        }
      });
      
      await batch.commit();
      await Promise.allSettled(storagePromises);
      
      await loadAllPhotos();
      setMessage(`${photosToDelete.length} oude foto's opgeruimd!`);
      
    } catch (error) {
      setMessage("Fout bij opruimen foto's: " + error.message);
    }
  };

  // Tab wijzigen
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPhotos(allPhotos[tab]);
  };

  // Datum formatteren
  const formatDate = (date) => {
    if (!date) return 'Onbekend';
    return date.toLocaleDateString('nl-NL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Laad foto's bij mount en tab wijziging
  useEffect(() => {
    loadAllPhotos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopId, reloadTick]);

  useEffect(() => {
    setPhotos(allPhotos[activeTab]);
  }, [activeTab, allPhotos]);

  return (
    <>
    <div style={{ padding: "20px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <h3 style={{ margin: 0 }}>Foto Moderatie</h3>
        <div style={{ display: "flex", gap: "10px" }}>
          <button
            onClick={loadAllPhotos}
            disabled={loading}
            style={{ 
              padding: "8px 16px", 
              background: "#007bff", 
              color: "white", 
              border: "none", 
              borderRadius: "4px",
              cursor: loading ? "not-allowed" : "pointer"
            }}
          >
            {loading ? "Laden..." : "🔄 Vernieuwen"}
          </button>
          <button 
            onClick={cleanupOldRejectedPhotos}
            style={{ 
              padding: "8px 16px", 
              background: "#dc3545", 
              color: "white", 
              border: "none", 
              borderRadius: "4px",
              cursor: "pointer"
            }}
          >
            🗑️ Oude Foto's Opruimen
          </button>
        </div>
      </div>

      {message && (
        <div role="status" aria-live="polite" style={{ 
          padding: "10px", 
          borderRadius: "4px",
          background: message.includes("Fout") ? "#f8d7da" : "#d4edda",
          color: message.includes("Fout") ? "#721c24" : "#155724",
          border: `1px solid ${message.includes("Fout") ? "#f5c6cb" : "#c3e6cb"}`,
          marginBottom: "20px"
        }}>
          {message}
          {indexBuilding && (
            <div style={{ marginTop: 6, fontSize: 13, color: '#856404' }}>
              {indexUrl ? (
                <a href={indexUrl} target="_blank" rel="noreferrer">Open index status</a>
              ) : (
                <span>Index aan het bouwen...</span>
              )}
              <span style={{ marginLeft: 6 }}>
                {retryAttempts <= 6 ? `Automatische poging over ${retryIn}s.` : 'Automatische pogingen gestopt.'}
              </span>
              <button onClick={loadAllPhotos} className="btn btn-secondary" style={{ marginLeft: 10, padding: '2px 8px' }}>Opnieuw proberen</button>
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", marginBottom: "20px", borderBottom: "1px solid #ddd" }}>
        <button 
          onClick={() => handleTabChange("pending")}
          style={{ 
            padding: "10px 20px", 
            background: activeTab === "pending" ? "#ffc107" : "transparent",
            color: activeTab === "pending" ? "black" : "#ffc107",
            border: "none",
            cursor: "pointer",
            borderBottom: activeTab === "pending" ? "2px solid #ffc107" : "none"
          }}
        >
          ⏳ Wachtend ({allPhotos.pending.length})
        </button>
        <button 
          onClick={() => handleTabChange("approved")}
          style={{ 
            padding: "10px 20px", 
            background: activeTab === "approved" ? "#28a745" : "transparent",
            color: activeTab === "approved" ? "white" : "#28a745",
            border: "none",
            cursor: "pointer",
            borderBottom: activeTab === "approved" ? "2px solid #28a745" : "none"
          }}
        >
          ✅ Goedgekeurd ({allPhotos.approved.length})
        </button>
        <button 
          onClick={() => handleTabChange("rejected")}
          style={{ 
            padding: "10px 20px", 
            background: activeTab === "rejected" ? "#dc3545" : "transparent",
            color: activeTab === "rejected" ? "white" : "#dc3545",
            border: "none",
            cursor: "pointer",
            borderBottom: activeTab === "rejected" ? "2px solid #dc3545" : "none"
          }}
        >
          ❌ Afgekeurd ({allPhotos.rejected.length})
        </button>
      </div>

      {/* Foto Grid */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <p>Foto's laden...</p>
        </div>
      ) : photos.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
          <p>Geen foto's in deze categorie.</p>
        </div>
      ) : (
        <div style={{ 
          display: "grid", 
          gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", 
          gap: "20px" 
        }}>
          {photos.map((photo) => (
            <div key={photo.id} style={{
              background: "white",
              borderRadius: "8px",
              overflow: "hidden",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              position: "relative"
            }}>
              {/* Foto */}
              <div style={{ position: "relative", height: "200px", overflow: "hidden" }}>
                <img 
                  src={photo.photoUrl} 
                  alt="Upload"
                  style={{ 
                    width: "100%", 
                    height: "100%", 
                    objectFit: "cover" 
                  }}
                />
                
                {/* Status overlay */}
                <div style={{
                  position: "absolute",
                  top: "10px",
                  right: "10px",
                  padding: "4px 8px",
                  borderRadius: "12px",
                  fontSize: "12px",
                  fontWeight: "bold",
                  background: photo.status === 'approved' ? "#28a745" : 
                            photo.status === 'rejected' ? "#dc3545" : "#ffc107",
                  color: photo.status === 'approved' ? "white" : 
                         photo.status === 'rejected' ? "white" : "black"
                }}>
                  {photo.status === 'approved' ? '✅ Goedgekeurd' : 
                   photo.status === 'rejected' ? '❌ Afgekeurd' : '⏳ Wachtend'}
                </div>
              </div>
              
              {/* Info */}
              <div style={{ padding: "15px" }}>
                <p style={{ margin: "0 0 10px 0", fontSize: "14px" }}>
                  <strong>Gebruiker:</strong> {photo.userName}
                </p>
                <p style={{ margin: "0 0 10px 0", fontSize: "14px" }}>
                  <strong>Datum:</strong> {formatDate(photo.createdAt)}
                </p>
                
                {/* Acties */}
                <div style={{ display: "flex", gap: "5px", flexWrap: "wrap" }}>
                  {photo.status === 'pending' && (
                    <>
                      <button 
                        onClick={() => changePhotoStatus(photo.id, 'approved')}
                        style={{
                          padding: "6px 12px",
                          background: "#28a745",
                          color: "white",
                          border: "none",
                          borderRadius: "3px",
                          cursor: "pointer",
                          fontSize: "12px"
                        }}
                      >
                        ✅ Goedkeuren
                      </button>
                      <button 
                        onClick={() => changePhotoStatus(photo.id, 'rejected')}
                        style={{
                          padding: "6px 12px",
                          background: "#dc3545",
                          color: "white",
                          border: "none",
                          borderRadius: "3px",
                          cursor: "pointer",
                          fontSize: "12px"
                        }}
                      >
                        ❌ Afkeuren
                      </button>
                    </>
                  )}
                  
                  {photo.status === 'approved' && (
                    <button 
                      onClick={() => changePhotoStatus(photo.id, 'rejected')}
                      style={{
                        padding: "6px 12px",
                        background: "#dc3545",
                        color: "white",
                        border: "none",
                        borderRadius: "3px",
                        cursor: "pointer",
                        fontSize: "12px"
                      }}
                    >
                      ❌ Afkeuren
                    </button>
                  )}
                  
                  {photo.status === 'rejected' && (
                    <>
                      <button 
                        onClick={() => changePhotoStatus(photo.id, 'approved')}
                        style={{
                          padding: "6px 12px",
                          background: "#28a745",
                          color: "white",
                          border: "none",
                          borderRadius: "3px",
                          cursor: "pointer",
                          fontSize: "12px"
                        }}
                      >
                        ✅ Goedkeuren
                      </button>
                      <button 
                        onClick={() => deletePhoto(photo.id, photo.photoUrl)}
                        style={{
                          padding: "6px 12px",
                          background: "#6c757d",
                          color: "white",
                          border: "none",
                          borderRadius: "3px",
                          cursor: "pointer",
                          fontSize: "12px"
                        }}
                      >
                        🗑️ Verwijderen
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
    <ConfirmDialog
      open={!!confirmState}
      title={confirmState?.title}
      message={confirmState?.message}
      danger={confirmState?.danger}
      confirmLabel={confirmState?.confirmLabel}
      onConfirm={confirmState?.onConfirm}
      onCancel={() => setConfirmState(null)}
    />
    </>
  );
};

export default PhotoModeration;